// src/utils/projectStateUtils.ts

import { ProjectState } from '../types/project';
import { Chapter, ChapterVitals, WorldBuildingData, FrontMatter, BackMatter, PublishingEssentials, AuthorDetails } from '../types';
import { LOCAL_STORAGE_ACTIVE_PROJECT_KEY, initialProjectState, initialChapterVitals, initialFrontMatter, initialBackMatter, initialPublishingEssentials, initialAuthorDetails } from '../constants';

/**
 * Normalizes a ProjectState to ensure all array fields exist and are properly initialized.
 * This prevents runtime crashes from undefined array access.
 */
export function normalizeProjectState(state: ProjectState): ProjectState {
    if (!state) return state;
    
    const rawChapters = Array.isArray(state.chapters) ? state.chapters : [];
    // Normalize chapters: ensure array and normalize chapter history arrays
    const normalizedChapters = rawChapters.map(chapter => ({
        ...chapter,
        history: Array.isArray(chapter.history) ? chapter.history : []
    }));
    return {
        ...state,
        // Ensure chapters is always an array with normalized history
        chapters: normalizedChapters,

        // Ensure selectedChapterIdsForMerge is always an array
        selectedChapterIdsForMerge: Array.isArray(state.selectedChapterIdsForMerge)
            ? state.selectedChapterIdsForMerge : [],

        // Ensure blueprint and its nested structure exist
        blueprint: {
            ...state.blueprint,
            bookMetadata: state.blueprint?.bookMetadata || {},
            worldBuildingSuggestions: {
// FIX: Replaced direct property access with optional chaining to prevent runtime errors when 'blueprint.worldBuildingSuggestions' is missing or undefined.
                ...state.blueprint?.worldBuildingSuggestions,
                storyTitleIdeas: Array.isArray(state.blueprint?.worldBuildingSuggestions?.storyTitleIdeas)
                    ? state.blueprint.worldBuildingSuggestions.storyTitleIdeas : [],
                characters: Array.isArray(state.blueprint?.worldBuildingSuggestions?.characters)
                    ? state.blueprint.worldBuildingSuggestions.characters : [],
                locations: Array.isArray(state.blueprint?.worldBuildingSuggestions?.locations)
                    ? state.blueprint.worldBuildingSuggestions.locations : [],
                customCategories: Array.isArray(state.blueprint?.worldBuildingSuggestions?.customCategories)
                    ? state.blueprint.worldBuildingSuggestions.customCategories : [],
                themes: Array.isArray(state.blueprint?.worldBuildingSuggestions?.themes)
                    ? state.blueprint.worldBuildingSuggestions.themes : [],
                selectedStoryTitle: state.blueprint?.worldBuildingSuggestions?.selectedStoryTitle || '',
                logLine: state.blueprint?.worldBuildingSuggestions?.logLine || '',
                storyStartConcept: state.blueprint?.worldBuildingSuggestions?.storyStartConcept || '',
                storyEndConcept: state.blueprint?.worldBuildingSuggestions?.storyEndConcept || '',
            }
        },
    };
}

/**
 * Loads the active project from localStorage or returns the initial default state.
 * This function includes data hydration and normalization for older project structures.
 */
export const loadInitialProjectState = (): ProjectState => {
    const savedActive = localStorage.getItem(LOCAL_STORAGE_ACTIVE_PROJECT_KEY);
    try {
        if (savedActive) {
            const parsed = JSON.parse(savedActive);

            const chaptersWithInitializedVitals = (parsed.chapters || []).map((ch: any): Chapter => ({
                ...(initialProjectState.chapters?.find((c: any) => c.id === '') || {}),
                ...ch,
                id: ch.id || `load-ch-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                history: ch.history || [],
                historyIndex: ch.historyIndex !== undefined ? ch.historyIndex : -1,
                status: ch.status || 'draft',
                expectedWordCount: ch.expectedWordCount || 0,
                vitals: { ...(initialChapterVitals || {}), ...(ch.vitals || {}) } as ChapterVitals,
                previewContent: ch.previewContent || null,
                previewSummary: ch.previewSummary || null,
                previewWordCountTarget: ch.previewWordCountTarget || 0,
            }));

            const worldData: WorldBuildingData = {
                ...(initialProjectState.blueprint?.worldBuildingSuggestions),
                ...(parsed.worldBuildingData || {})
            };
            worldData.storyTitleIdeas = worldData.storyTitleIdeas || [];
            worldData.selectedStoryTitle = worldData.selectedStoryTitle || (worldData.storyTitleIdeas.length > 0 ? worldData.storyTitleIdeas[0] : '');
            worldData.logLine = worldData.logLine || '';
            worldData.storyStartConcept = worldData.storyStartConcept || '';
            worldData.storyEndConcept = worldData.storyEndConcept || '';
            worldData.characters = (worldData.characters || []).map((c: any, i: number) => ({
                id: c.id || `load-char-${Date.now()}-${i}`,
                name: c.name || '',
                description: c.description || c.briefDescription || '',
            })) as any;
            worldData.locations = (worldData.locations || []).map((l: any, i: number) => ({
                id: l.id || `load-loc-${Date.now()}-${i}`,
                name: l.name || '',
                briefDescription: l.briefDescription || '',
            }));
            worldData.customCategories = (worldData.customCategories || []).map((cc: any, i: number) => ({
                id: cc.id || `load-customcat-${Date.now()}-${i}`,
                categoryName: cc.categoryName || 'Unnamed Category',
                ...cc,
                items: (cc.items || []).map((item: any, j: number) => ({
                    id: item.id || `load-customitem-${Date.now()}-${i}-${j}`,
                    name: item.name || '',
                    description: item.description || '',
                }))
            }));

            const authorDetailsData: AuthorDetails = {
                ...initialAuthorDetails,
                ...(parsed.authorDetails || {}),
                authorProfileImageBase64: parsed.authorDetails?.authorProfileImageBase64 || null,
            };

            const frontMatterData: FrontMatter = {
                ...initialFrontMatter,
                ...(parsed.frontMatter || {}),
                titlePageImageBase64: parsed.frontMatter?.titlePageImageBase64 || null,
            };

            const backMatterData: BackMatter = {
                ...initialBackMatter,
                ...(parsed.backMatter || {}),
                backCoverImageBase64: parsed.backMatter?.backCoverImageBase64 || null,
                appendices: (parsed.backMatter?.appendices || []).map((app: any, i: number) => ({
                    id: app.id || `load-app-${Date.now()}-${i}`,
                    title: app.title || '',
                    content: app.content || ''
                })),
                glossary: (parsed.backMatter?.glossary || []).map((gl: any, i: number) => ({
                    id: gl.id || `load-gls-${Date.now()}-${i}`,
                    term: gl.term || '',
                    definition: gl.definition || ''
                })),
                bibliography: (parsed.backMatter?.bibliography || []).map((bib: any, i: number) => ({
                    id: bib.id || `load-bib-${Date.now()}-${i}`,
                    citation: bib.citation || ''
                })),
            };

            const publishingEssentialsData: PublishingEssentials = {
                ...initialPublishingEssentials,
                ...(parsed.publishingEssentials || {})
            };


            const hydratedState: ProjectState = {
                ...initialProjectState,
                ...parsed,
                chapters: chaptersWithInitializedVitals,
                bookMetadata: { ...(initialProjectState.bookMetadata), ...(parsed.bookMetadata || {}) },
                blueprint: {
                    ...initialProjectState.blueprint,
                    worldBuildingSuggestions: worldData
                },
                authorDetails: authorDetailsData,
                frontMatter: frontMatterData,
                backMatter: backMatterData,
                publishingEssentials: publishingEssentialsData,
                selectedChapterIdsForMerge: parsed.selectedChapterIdsForMerge || [],
                exportChapterContentPreference: parsed.exportChapterContentPreference || initialProjectState.exportChapterContentPreference,
                isUnsaved: false, // loaded project is considered saved initially
                bookMetadataSnapshotForChapters: parsed.bookMetadataSnapshotForChapters || null,
                blueprintSnapshotForChapters: parsed.blueprintSnapshotForChapters || null,
                blueprintSnapshotForHolisticRegen: parsed.blueprintSnapshotForHolisticRegen || null,
                 hasInitialBlueprint: parsed.hasInitialBlueprint !== undefined ? parsed.hasInitialBlueprint :
                                    !!(parsed.worldBuildingData?.selectedStoryTitle || parsed.outlineIdea ||
                                       (parsed.bookMetadata && Object.keys(parsed.bookMetadata).length > 0)),
            };
            return normalizeProjectState(hydratedState);
        }
    } catch (e) {
        console.error("Error parsing saved project state:", e);
    }

    const defaultState: ProjectState = {
        ...initialProjectState,
        outlineIdea: '',
        // FIX: Object literal may only specify known properties, and 'bookType' does not exist in type 'ProjectState'.
        // workType not in ProjectState type, using known fields only
        targetPageCount: '',
        activeChapterId: null,
        currentProjectName: null,
        isUnsaved: false,
        hasInitialBlueprint: false,
        selectedChapterIdsForMerge: [],
        exportChapterContentPreference: 'content',
    };
    return normalizeProjectState(defaultState);
};

export function getChapterContent(
  ch: Chapter,
  mode: 'preview' | 'content'
): string {
  // For preview mode, always return preview content if available
  if (mode === 'preview' && ch.previewContent) {
      return ch.previewContent;
  }
  
  // For content mode, try to get full text from history first
  const v = (ch.history ?? [])[ch.historyIndex ?? -1];
  if (v && v.content) {
      return v.content;
  }
  
  // Fallback: If no full text is available, return preview content as fallback
  if (ch.previewContent) {
      return ch.previewContent;
  }
  
  // Last resort: return empty content
  return '';
}